import { EditorView } from "@codemirror/view";
import { HighlightStyle, syntaxHighlighting } from "@codemirror/language";
import { tags } from "@lezer/highlight";

/**
 * Warm light theme matching Anthropic's cream palette.
 * Used for the code editor in light mode.
 */
export const warmLightTheme = EditorView.theme(
  {
    "&": {
      backgroundColor: "#fafaf8",
      color: "#1f2937",
      fontSize: "14px",
      fontFamily: "var(--font-geist-mono), ui-monospace, monospace",
    },
    ".cm-content": {
      padding: "12px 0",
      caretColor: "#0e0e0e",
    },
    ".cm-gutters": {
      backgroundColor: "#f5f5f0",
      color: "#9ca3af",
      border: "none",
      borderRight: "1px solid #e5e3d9",
      paddingRight: "8px",
    },
    ".cm-lineNumbers .cm-gutterElement": {
      padding: "0 8px 0 16px",
      minWidth: "40px",
      textAlign: "right",
    },
    ".cm-activeLineGutter": {
      backgroundColor: "rgba(238, 236, 226, 0.8)",
      color: "#374151",
    },
    ".cm-activeLine": {
      backgroundColor: "rgba(238, 236, 226, 0.5)",
    },
    ".cm-cursor": {
      borderLeftColor: "#0e0e0e",
      borderLeftWidth: "2px",
    },
    ".cm-selectionBackground": {
      backgroundColor: "rgba(238, 236, 226, 0.8)",
    },
    "&.cm-focused .cm-selectionBackground": {
      backgroundColor: "rgba(238, 236, 226, 0.9)",
    },
    ".cm-matchingBracket": {
      backgroundColor: "rgba(238, 236, 226, 0.8)",
      outline: "1px solid #d1d5db",
    },
    ".cm-searchMatch": {
      backgroundColor: "#fef3c7",
      outline: "1px solid #fcd34d",
    },
    ".cm-searchMatch.cm-searchMatch-selected": {
      backgroundColor: "#fde68a",
    },
    ".cm-foldPlaceholder": {
      backgroundColor: "#f3f4f6",
      border: "1px solid #e5e7eb",
      borderRadius: "4px",
      color: "#6b7280",
      padding: "0 4px",
    },
  },
  { dark: false }
);

/**
 * Syntax highlighting for light theme
 */
export const warmLightHighlighting = HighlightStyle.define([
  { tag: tags.keyword, color: "#7c3aed" },
  { tag: tags.comment, color: "#6b7280", fontStyle: "italic" },
  { tag: tags.string, color: "#059669" },
  { tag: tags.number, color: "#0284c7" },
  { tag: tags.function(tags.variableName), color: "#2563eb" },
  { tag: tags.typeName, color: "#0891b2" },
  { tag: tags.className, color: "#0891b2" },
  { tag: tags.definition(tags.variableName), color: "#1f2937" },
  { tag: tags.propertyName, color: "#1f2937" },
  { tag: tags.operator, color: "#6b7280" },
  { tag: tags.punctuation, color: "#6b7280" },
  { tag: tags.bool, color: "#7c3aed" },
  { tag: tags.null, color: "#7c3aed" },
  { tag: tags.regexp, color: "#db2777" },
  { tag: tags.escape, color: "#db2777" },
  { tag: tags.special(tags.string), color: "#059669" },
  { tag: tags.tagName, color: "#dc2626" },
  { tag: tags.attributeName, color: "#ca8a04" },
]);

/**
 * GitHub Dimmed dark theme matching the app's dark mode.
 */
export const githubDimmedTheme = EditorView.theme(
  {
    "&": {
      backgroundColor: "#22272e",
      color: "#adbac7",
      fontSize: "14px",
      fontFamily: "var(--font-geist-mono), ui-monospace, monospace",
    },
    ".cm-content": {
      padding: "12px 0",
      caretColor: "#539bf5",
    },
    ".cm-gutters": {
      backgroundColor: "#2d333b",
      color: "#636e7b",
      border: "none",
      borderRight: "1px solid #444c56",
      paddingRight: "8px",
    },
    ".cm-lineNumbers .cm-gutterElement": {
      padding: "0 8px 0 16px",
      minWidth: "40px",
      textAlign: "right",
    },
    ".cm-activeLineGutter": {
      backgroundColor: "rgba(55, 62, 71, 0.8)",
      color: "#adbac7",
    },
    ".cm-activeLine": {
      backgroundColor: "rgba(55, 62, 71, 0.5)",
    },
    ".cm-cursor": {
      borderLeftColor: "#539bf5",
      borderLeftWidth: "2px",
    },
    ".cm-selectionBackground": {
      backgroundColor: "rgba(83, 155, 245, 0.2)",
    },
    "&.cm-focused .cm-selectionBackground": {
      backgroundColor: "rgba(83, 155, 245, 0.3)",
    },
    ".cm-matchingBracket": {
      backgroundColor: "rgba(83, 155, 245, 0.2)",
      outline: "1px solid #539bf5",
    },
    ".cm-searchMatch": {
      backgroundColor: "rgba(198, 144, 38, 0.3)",
      outline: "1px solid #c69026",
    },
    ".cm-searchMatch.cm-searchMatch-selected": {
      backgroundColor: "rgba(198, 144, 38, 0.5)",
    },
    ".cm-foldPlaceholder": {
      backgroundColor: "#373e47",
      border: "1px solid #444c56",
      borderRadius: "4px",
      color: "#768390",
      padding: "0 4px",
    },
  },
  { dark: true }
);

/**
 * Syntax highlighting for dark theme
 */
export const githubDimmedHighlighting = HighlightStyle.define([
  { tag: tags.keyword, color: "#f47067" },
  { tag: tags.comment, color: "#768390", fontStyle: "italic" },
  { tag: tags.string, color: "#96d0ff" },
  { tag: tags.number, color: "#6cb6ff" },
  { tag: tags.function(tags.variableName), color: "#dcbdfb" },
  { tag: tags.typeName, color: "#8ddb8c" },
  { tag: tags.className, color: "#8ddb8c" },
  { tag: tags.definition(tags.variableName), color: "#adbac7" },
  { tag: tags.propertyName, color: "#adbac7" },
  { tag: tags.operator, color: "#f47067" },
  { tag: tags.punctuation, color: "#768390" },
  { tag: tags.bool, color: "#f47067" },
  { tag: tags.null, color: "#f47067" },
  { tag: tags.regexp, color: "#96d0ff" },
  { tag: tags.escape, color: "#96d0ff" },
  { tag: tags.special(tags.string), color: "#96d0ff" },
  { tag: tags.tagName, color: "#8ddb8c" },
  { tag: tags.attributeName, color: "#f69d50" },
]);

/**
 * Combined extensions for light theme
 */
export const lightThemeExtensions = [
  warmLightTheme,
  syntaxHighlighting(warmLightHighlighting),
];

/**
 * Combined extensions for dark theme
 */
export const darkThemeExtensions = [
  githubDimmedTheme,
  syntaxHighlighting(githubDimmedHighlighting),
];
